// Bütün Aramalar — bir müşteri ID'sine yapılan HER çağrı (görev #193 Vegas / #194 Prime, asya 2026-10-09:
// "admin&spv's can search an id there and we will see every call has been made to that user").
// ─────────────────────────────────────────────────────────────────────────────
// Veri GET /api/customer-calls (src/api/customer-calls.js): call_events (sheet kırpılsa da her günü tutan kayıt) +
// sonucu kaydedilmemiş softphone çağrıları (call_sessions). Marka başına tek arama — müşteri ID'leri platforma özgü.
// Süpervizör yalnız KENDİ markalarını arar (sunucu kapsamı; kapsam dışı marka 404).
//   §1 veri yalnız api.ts · §2 TR kaynak + EN kaplama (i18n.ts) · §6 ölçülmeyen değer BOŞ.

import { useEffect, useMemo, useState } from 'react';
import { ApiError, getCustomerCalls, getStatusSystems } from '../api';
import { brandLabel } from '../constants';
import { downloadCSV, fmtDate, fmtHMS, fmtMoney } from '../format';
import type { BrandId, CustomerCallRow, CustomerCallsResult, Lang } from '../types';
import { cardStyle, CsvButton, dotStyle, EmptyState, Icon, inputStyle, primaryBtnStyle, Select, Skeleton } from '../ui';

/** Sunucu eşi: src/api/customer-calls.js CUSTOMER_CALL_BRANDS (BetBooker panelde emekli). */
export const CUSTOMER_CALL_BRANDS: BrandId[] = ['vegas', 'prime', 'primevip', 'hera'];

// Sütun kimliği sheet'in D sütunu: neredeyse hep sayısal BackOffice ID'si, nadiren kullanıcı adı. Sunucu ID_RE'nin eşi.
const ID_RE = /^[\p{L}\p{N}._-]{1,40}$/u;

const COLS = '96px 52px minmax(90px,150px) minmax(80px,120px) minmax(150px,200px) 64px minmax(120px,1fr) 76px';

const ERROR_NOTE: Record<string, string> = {
  bad_id: 'Geçersiz müşteri ID',
  bad_brand: 'Bu marka için arama yapılamaz',
  not_found: 'Bu markayı görme yetkiniz yok',
};

const SOURCE_META: Record<CustomerCallRow['source'], { label: string; color: string; bg: string; border: string }> = {
  tool: { label: 'Araç', color: 'var(--dim)', bg: 'var(--surface2)', border: 'var(--border)' },
  manual: { label: 'Manuel', color: '#e0a23c', bg: 'rgba(224,162,60,.14)', border: 'rgba(224,162,60,.3)' },
  bot: { label: 'Bot', color: '#a97cf0', bg: 'rgba(169,124,240,.14)', border: 'rgba(169,124,240,.3)' },
};

const NO_OUTCOME_LABEL = 'Sonuç kaydedilmedi';

/** Yalnız sayı + birim taşıyan title — EN karşılığı translatePhrase'deki regex. */
function ringTitle(c: CustomerCallRow): string | undefined {
  if (c.answered === null) return undefined;
  return c.answered ? 'Bağlandı' : c.ringSec != null ? 'Bağlanmadı · çalma ' + fmtHMS(c.ringSec) : 'Bağlanmadı';
}

export default function CustomerCalls({ brands, lang }: { brands: BrandId[]; lang: Lang }) {
  // Marka: üst filtre ∩ aranabilir markalar; boş kesişimde hepsi (sunucu yine kapsamı uygular).
  const brandOptions = useMemo(() => {
    const inScope = CUSTOMER_CALL_BRANDS.filter((b) => brands.includes(b));
    return inScope.length ? inScope : CUSTOMER_CALL_BRANDS;
  }, [brands]);
  const [brand, setBrand] = useState<BrandId>(brandOptions[0]);
  useEffect(() => {
    if (!brandOptions.includes(brand)) setBrand(brandOptions[0]);
  }, [brandOptions, brand]);

  const [customerId, setCustomerId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<CustomerCallsResult | null>(null);

  const idOk = ID_RE.test(customerId.trim());

  const run = async () => {
    if (!idOk || busy) return;
    setBusy(true);
    setError('');
    try {
      setData(await getCustomerCalls(brand, customerId.trim()));
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'request_failed';
      setError(ERROR_NOTE[msg] || 'Aramalar alınamadı — ' + msg);
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  const smap = useMemo(() => {
    const m: Record<string, { label: string; color: string }> = {};
    getStatusSystems().tr.forEach((x) => (m[x.key] = x));
    return m;
  }, []);
  const st = (c: CustomerCallRow) => (c.outcomeSaved
    ? smap[c.result] || { label: c.result, color: 'var(--dim)' }
    : { label: NO_OUTCOME_LABEL, color: 'var(--faint)' });

  const calls = data?.calls || [];
  const summary = useMemo(() => {
    const agents = new Set(calls.map((c) => c.agent).filter(Boolean));
    return {
      total: calls.length,
      agents: agents.size,
      noOutcome: calls.filter((c) => !c.outcomeSaved).length,
      first: calls.length ? calls[calls.length - 1] : null,
      last: calls.length ? calls[0] : null,
    };
  }, [calls]);

  const onCsv = () => {
    if (!data) return;
    const head = ['Tarih', 'Saat', 'Temsilci', 'Liste', 'Sonuc', 'Konusma', 'Yorum', 'Kaynak'];
    downloadCSV(
      'butun-aramalar-' + data.brand + '-' + data.customerId + '.csv',
      [head as (string | number)[]].concat(
        calls.map((c) => [
          fmtDate(c.day), c.time, c.agent, c.list, st(c).label,
          c.talkSec != null ? fmtHMS(c.talkSec) : '', c.comment, SOURCE_META[c.source].label,
        ]),
      ),
    );
  };

  const headCell: React.CSSProperties = { fontSize: '10.5px', fontWeight: 600, letterSpacing: '.05em', color: 'var(--faint)' };
  const cell: React.CSSProperties = { fontSize: '12px', color: 'var(--dim)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <>
      {/* sorgu */}
      <div style={{ ...cardStyle, padding: '16px 18px', marginBottom: '18px', display: 'flex', alignItems: 'flex-end', gap: '12px', flexWrap: 'wrap' }}>
        {brandOptions.length > 1 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            <span style={headCell}>MARKA</span>
            <Select value={brand} onChange={(v) => setBrand(v as BrandId)} options={brandOptions.map((b) => ({ value: b, label: brandLabel(b) }))} />
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', flex: '0 1 260px' }}>
          <span style={headCell}>MÜŞTERİ ID</span>
          <input
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value.trim())}
            onKeyDown={(e) => { if (e.key === 'Enter') void run(); }}
            placeholder="Müşteri ID"
            autoFocus
            style={{ ...inputStyle, padding: '9px 12px', fontSize: '13px' }}
          />
        </div>
        <button onClick={() => void run()} disabled={!idOk || busy} style={{ ...primaryBtnStyle, padding: '10px 18px', opacity: !idOk || busy ? 0.55 : 1, cursor: !idOk || busy ? 'default' : 'pointer' }}>
          {busy ? 'Aranıyor…' : 'Ara'}
        </button>
        {brandOptions.length === 1 && <span style={{ marginLeft: 'auto', fontSize: '12px', color: 'var(--dim)' }}>{brandLabel(brand)}</span>}
      </div>

      {error && (
        <div style={{ ...cardStyle, padding: '12px 16px', marginBottom: '14px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '12.5px', color: '#ef5d5d' }}>
          <Icon name="error" size={18} color="#ef5d5d" />
          <span>{error}</span>
        </div>
      )}

      {busy && !data && <Skeleton heights={[70, 260]} />}

      {!busy && !data && !error && (
        <EmptyState icon="manage_search" title="Bütün Aramalar" reason="Müşteri ID'sini yazıp Ara'ya basın. O müşteriye yapılan her çağrı — hangi temsilci, hangi liste, sonuç, konuşma süresi ve yorum — en yeniden eskiye listelenir." />
      )}

      {data && !calls.length && (
        <EmptyState icon="phone_disabled" title="Çağrı bulunamadı" reason={brandLabel(data.brand) + ' markasında ' + data.customerId + ' numaralı müşteriye kayıtlı çağrı yok.'} />
      )}

      {data && calls.length > 0 && (
        <div style={{ opacity: busy ? 0.6 : 1, transition: 'opacity .15s' }}>
          {/* özet */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '12px', marginBottom: '16px' }}>
            <SummaryTile title="Toplam çağrı" value={fmtMoney(summary.total)} note={summary.noOutcome ? 'Sonucu kaydedilmeyen: ' + summary.noOutcome : undefined} />
            <SummaryTile title="Temsilci" value={fmtMoney(summary.agents)} />
            <SummaryTile title="İlk çağrı" value={summary.first ? fmtDate(summary.first.day) + ' ' + summary.first.time : ''} />
            <SummaryTile title="Son çağrı" value={summary.last ? fmtDate(summary.last.day) + ' ' + summary.last.time : ''} />
          </div>

          <div style={{ ...cardStyle, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text)' }}>{'#' + data.customerId}</div>
                <div style={{ fontSize: '11.5px', color: 'var(--faint)' }}>{brandLabel(data.brand)}</div>
              </div>
              <CsvButton onClick={onCsv} />
            </div>
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: '860px' }}>
                <div lang={lang} style={{ display: 'grid', gridTemplateColumns: COLS, gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', ...headCell }}>
                  <span>TARİH</span>
                  <span>SAAT</span>
                  <span>TEMSİLCİ</span>
                  <span>LİSTE</span>
                  <span>SONUÇ</span>
                  <span style={{ textAlign: 'right' }}>KONUŞMA</span>
                  <span>YORUM</span>
                  <span>KAYNAK</span>
                </div>
                {calls.map((c) => {
                  const s = st(c);
                  const src = SOURCE_META[c.source];
                  return (
                    <div key={c.id} style={{ display: 'grid', gridTemplateColumns: COLS, gap: '10px', padding: '9px 18px', borderBottom: '1px solid var(--border)', alignItems: 'center' }}>
                      <span style={cell}>{fmtDate(c.day)}</span>
                      <span style={cell}>{c.time}</span>
                      {/* Sheet/kayıt değerleri (temsilci, liste) çevrilmez. */}
                      <span style={{ ...cell, color: 'var(--text)', fontWeight: 500 }}>{c.agent}</span>
                      <span style={cell}>{c.list}</span>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 600, color: s.color, minWidth: 0 }}>
                        <span style={dotStyle(s.color)} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{s.label}</span>
                      </span>
                      <span title={ringTitle(c)} style={{ ...cell, textAlign: 'right' }}>{c.talkSec != null ? fmtHMS(c.talkSec) : ''}</span>
                      <span title={c.comment || undefined} style={cell}>{c.comment}</span>
                      <span>
                        <span style={{ fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', whiteSpace: 'nowrap', color: src.color, background: src.bg, border: '1px solid ' + src.border }}>
                          {src.label}
                        </span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <div style={{ padding: '10px 18px', fontSize: '11.5px', color: 'var(--faint)', lineHeight: 1.5 }}>
              {data.truncated
                ? 'En yeni ' + data.limit + ' çağrı gösteriliyor; daha eskileri var.'
                : '"Sonuç kaydedilmedi" = softphone ile arandı ama sonuç girilmedi. Bot = DepositWatchdog satırı, temsilcinin çağrısı değil.'}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function SummaryTile({ title, value, note }: { title: string; value: string; note?: string }) {
  return (
    <div style={{ ...cardStyle, padding: '13px 16px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '.04em', color: 'var(--faint)' }}>{title}</div>
      <div style={{ fontSize: '19px', fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {note ? <div style={{ fontSize: '11.5px', color: 'var(--dim)' }}>{note}</div> : null}
    </div>
  );
}
